import Clock from "app/components/Icons/Clock";
import { DatePicker } from "app/containers/Features/components/TimeTracker/DatePicker";
import { isoToTimeSlot } from "app/containers/Features/components/TimeTracker/helpers";
import TimePickerInput from "app/containers/Features/components/TimeTracker/TimePickerInput";
import { ticketDetailsLocales } from "app/containers/Features/containers/Ticket/locales";
import { selectTicketDetails } from "app/containers/Features/containers/Ticket/redux/selectors";
import {
  selectActiveTimerForm,
  selectTimeTrackerForm,
} from "app/containers/Features/containers/TimeSheet/redux/selectors";
import { timeSheetActions } from "app/containers/Features/containers/TimeSheet/redux/slice";
import clsx from "clsx";
import { format } from "date-fns";
import React, { FC, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getDayDifferenceNumber,
  useTimeTracker,
} from "utils/hooks/useTimeTracker";

export interface DateTimePickerRowProps {
  containerClassName?: string;
  disabled?: boolean;
}

const DateTimePickerRow: FC<DateTimePickerRowProps> = ({
  containerClassName,
  disabled,
}) => {
  // Hooks
  const dispatch = useDispatch();
  const { isRunning, generateEndTimeSlots, generateFullDayTimeSlots } =
    useTimeTracker();

  // Global States
  const timeTrackerForm = useSelector(selectTimeTrackerForm);
  const activeTimerForm = useSelector(selectActiveTimerForm);
  const ticketDetails = useSelector(selectTicketDetails);

  // Variables
  const referenceDate = timeTrackerForm?.date
    ? new Date(timeTrackerForm.date as string)
    : new Date();

  const startTimeSlots = generateFullDayTimeSlots(referenceDate);
  const endTimeSlots = useCallback(() => {
    const start = new Date(timeTrackerForm.startTime);

    const hours = start.getHours();
    const minutes = start.getMinutes();

    const baseDate = new Date();

    baseDate.setHours(hours, minutes, 0, 0);

    return generateEndTimeSlots(baseDate);
  }, [timeTrackerForm?.startTime, generateEndTimeSlots]);

  const timerRunning =
    isRunning &&
    activeTimerForm.selectedTicketDetails._id === ticketDetails?._id;

  return (
    <div
      className={clsx(
        "border border-gray-100 border-t-0 px-4 py-2.5 flex items-center gap-1",
        containerClassName,
        disabled && "pointer-events-none opacity-50",
      )}
    >
      <div className="relative">
        <Clock size={24} />
      </div>
      <DatePicker
        value={
          timeTrackerForm?.date
            ? new Date(timeTrackerForm.date as string)
            : new Date()
        }
        onChange={(date: Date) => {
          dispatch(
            timeSheetActions.setEffortActualSingleValue({
              field: "date",
              value: format(date, "yyyy-MM-dd"),
            }),
          );
        }}
        mode="add"
      />
      <TimePickerInput
        mode="add"
        type="startTime"
        slots={startTimeSlots}
        referenceDate={referenceDate}
        value={isoToTimeSlot(timeTrackerForm?.startTime)}
        onChange={(slot) => {
          dispatch(
            timeSheetActions.setEffortActualSingleValue({
              field: "startTime",
              value: slot.iso,
            }),
          );
        }}
      />
      <span className="text-gray-300">-</span>
      {timerRunning ? (
        <span className="text-black text-sm leading-none px-3 py-2.5">
          {ticketDetailsLocales.now}
        </span>
      ) : (
        <TimePickerInput
          mode="add"
          type="endTime"
          slots={endTimeSlots()}
          referenceDate={referenceDate}
          value={isoToTimeSlot(timeTrackerForm?.endTime)}
          onChange={(slot) => {
            dispatch(
              timeSheetActions.setEffortActualSingleValue({
                field: "endTime",
                value: slot.iso,
              }),
            );
            dispatch(timeSheetActions.setEnableTimeTrackerPreview(true));
          }}
          futureDays={getDayDifferenceNumber(timeTrackerForm.startTime)}
        />
      )}
    </div>
  );
};

export default DateTimePickerRow;
